import { Photo, TagColor } from '@/types';
import { getTagColor } from '@/utils/mockData';
import { FileImage, Lock, Loader2 } from 'lucide-react';
import { ProcessingBadge } from './ProcessingBadge';
import { Checkbox } from '@/components/ui/checkbox';
import { useState } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';

interface PhotoCardProps {
  photo: Photo;
  isSelected: boolean;
  onSelect: (id: string) => void;
  onClick: () => void;
  onDoubleClick?: () => void;
}

export function PhotoCard({ photo, isSelected, onSelect, onClick, onDoubleClick }: PhotoCardProps) {
  const fileExtension = photo.filename.split('.').pop()?.toUpperCase() || 'IMG';
  const [imageLoading, setImageLoading] = useState(true);
  const isMobile = useIsMobile();
  
  // Only hide checkbox on confirmed mobile, not during initial undefined state
  const showCheckbox = isMobile === false;
  
  return (
    <div
      className={`
        group relative rounded-lg overflow-hidden cursor-pointer
        transition-smooth hover:scale-[1.02]
        ${isSelected ? 'ring-2 ring-primary' : ''}
      `}
      style={{
        boxShadow: isSelected 
          ? '0 0 0 2px hsl(var(--primary)), 0 8px 24px hsl(0 0% 0% / 0.4)'
          : '0 4px 12px hsl(0 0% 0% / 0.3)',
      }}
    >
      {/* Selection checkbox - hidden on mobile */}
      {showCheckbox && (
        <div 
          className="absolute top-2 left-2 z-10"
          onClick={(e) => {
            e.stopPropagation();
            onSelect(photo.id);
          }}
        >
          <Checkbox
            checked={isSelected}
            className="bg-background/80 backdrop-blur-sm"
          />
        </div>
      )}

      <div 
        onClick={onClick} 
        onDoubleClick={onDoubleClick}
        className="relative aspect-square bg-card"
      >
        {/* Image loading spinner */}
        {imageLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-muted z-[1]">
            <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
          </div>
        )}
        
        <img
          src={photo.url}
          alt={photo.filename}
          className="w-full h-full object-cover"
          style={{ imageRendering: 'crisp-edges' }}
          loading="lazy"
          decoding="async"
          onLoad={() => setImageLoading(false)}
          onError={() => setImageLoading(false)}
        />
        
        {/* PII indicator overlay */}
        {photo.has_pii && (
          <div className="absolute inset-0 bg-black/40 z-[5]" />
        )}

        {/* Processing status - top right */}
        <div className="absolute top-2 right-2 z-10">
          <ProcessingBadge status={(photo as any).processingStatus} />
        </div>

        {/* PII lock - bottom left */}
        {photo.has_pii && (
          <div className="absolute bottom-2 left-2 z-10">
            <div className="bg-black/70 backdrop-blur-sm px-2 py-1 rounded">
              <Lock className="w-4 h-4 text-white" />
            </div>
          </div>
        )}

        {/* File type badge - bottom right */}
        <div className="absolute bottom-2 right-2 z-10">
          <div className="bg-black/70 backdrop-blur-sm px-2 py-1 rounded text-xs font-medium">
            {fileExtension}
          </div>
        </div>
      </div>
    </div>
  );
}
